import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { Button, Spinner, TextField } from '@radix-ui/themes';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { Capacitor } from '@capacitor/core';
import type { ProfileSuccessResponse } from '@greatsumini/react-facebook-login';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import isEmail from 'validator/es/lib/isEmail';
import isEmpty from 'validator/es/lib/isEmpty';
import { facebookAppId } from 'helpers/facebookAppId';
import { errorToastMessage } from 'helpers/toaster';
import Analytics from 'ui/design-base/Analytics';
import { appleLogin, facebookLimitedLogin, facebookLogin, login } from 'store/authentication/authenticationSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import type { FacebookLoginType } from 'types/user';
import CapacitorFacebookLogin from './CapacitorFacebookLogin';
import { AppleIcon, FacebookIcon } from './SocialIcons';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [accessToken, setAccessToken] = useState('');
  const [jwtToken, setJwtToken] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fbEmail, setFbEmail] = useState('');
  const [uid, setUid] = useState('');
  const [photo, setPhoto] = useState('');

  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.authentication);

  const successFacebookResponse = (
    response:
      | {
          accessToken: string;
          expiresIn: string;
          reauthorize_required_in: string;
          signedRequest: string;
          userID: string;
        }
      | undefined,
  ) => {
    setAccessToken(response?.accessToken || '');
  };

  const successFacebookProfileResponse = (response: ProfileSuccessResponse) => {
    setFirstName(response.first_name);
    setLastName(response.last_name);
    setFbEmail(response.email as string);
    setUid(response.id as string);
    if (response.picture) setPhoto(response.picture.data.url);
  };

  useEffect(() => {
    if (jwtToken) {
      Analytics.sendEvent({ category: 'Login', action: 'Login with FB', label: 'Login Form' });
      const signUpFb = (token: string) => dispatch(facebookLimitedLogin(token));
      signUpFb(jwtToken);
    }
  }, [dispatch, jwtToken]);

  useEffect(() => {
    if (accessToken && firstName && lastName && fbEmail && uid && photo) {
      Analytics.sendEvent({ category: 'Login', action: 'Login with FB', label: 'Login Form' });
      const signUpFb = (user: FacebookLoginType) => dispatch(facebookLogin(user));
      signUpFb({
        token: accessToken,
        first_name: firstName,
        last_name: lastName,
        email: fbEmail,
        uid: uid,
        photo: photo,
      });
    }
  }, [accessToken, firstName, lastName, fbEmail, uid, photo, dispatch]);

  const isValid = () => [!isEmpty(email) && isEmail(email), !isEmpty(password)].every((valid) => valid);

  let content = <Spinner />;
  if (!loading) {
    content = (
      <div className='w-full md:w-96'>
        <div className='border-neutral-neutral-3 bg-neutral-neutral-1 mt-4 flex flex-col items-start gap-4 self-stretch rounded-2xl border border-solid p-3'>
          <div className='text-neutral-neutral-12 self-stretch text-center text-lg font-bold'>
            Accedi a TennisTalker
          </div>
          <div className='flex w-full flex-col gap-2'>
            <div className='text-neutral-neutral-12 self-stretch text-sm font-bold'>Email</div>
            <div className=''>
              <TextField.Root
                size='2'
                type='email'
                autoComplete='email'
                placeholder='indirizzo email'
                value={email}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    Analytics.sendEvent({ category: 'Login', action: 'Login with Enter', label: 'Login Form' });
                    dispatch(login({ email, password }));
                  }
                }}
              />
              {email !== '' && !isEmail(email) && (
                <p className='text-red-9 mb-0 text-sm'>questo indirizzo e-mail non sembra valido...</p>
              )}
            </div>
            <div className='text-neutral-neutral-12 self-stretch text-sm font-bold'>Password</div>
            <div className='mb-2'>
              <TextField.Root
                size='2'
                type='password'
                placeholder='password'
                autoComplete='current-password'
                value={password}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    Analytics.sendEvent({ category: 'Login', action: 'Login with Enter', label: 'Login Form' });
                    dispatch(login({ email, password }));
                  }
                }}
              />
            </div>

            <div className='flex w-full flex-col gap-2'>
              <Button
                className='h-10 w-full font-medium enabled:cursor-pointer'
                disabled={!isValid()}
                onClick={() => {
                  Analytics.sendEvent({ category: 'Login', action: 'Login with Button', label: 'Login Form' });
                  dispatch(login({ email, password }));
                }}
              >
                Accedi
              </Button>
              <div className='text-neutral-neutral-7 self-stretch text-center text-sm'>oppure</div>
              {Capacitor.isNativePlatform() ? (
                <CapacitorFacebookLogin
                  setAccessToken={setAccessToken}
                  successFacebookProfileResponse={successFacebookProfileResponse}
                  setJwtToken={setJwtToken}
                />
              ) : (
                <FacebookLogin
                  appId={facebookAppId() ?? ''}
                  autoLoad={false}
                  fields='first_name,last_name,name,email,picture.width(500).height(500),birthday'
                  className='h-10 w-full cursor-pointer rounded-md border-0 bg-[#1877F2]'
                  onProfileSuccess={successFacebookProfileResponse}
                  onSuccess={successFacebookResponse}
                >
                  <div className='flex items-center justify-center gap-3 text-white'>
                    <div>
                      <FacebookIcon />
                    </div>
                    Entra con Facebook
                  </div>
                </FacebookLogin>
              )}

              {(!Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'ios') && (
                <Button
                  className='flex h-10 w-full cursor-pointer items-center justify-center gap-3 rounded-md border-0 bg-black text-white'
                  onClick={() => {
                    SignInWithApple.authorize({
                      clientId: 'app.tennistalker.it',
                      scopes: 'email name',
                      redirectURI: 'https://www.tennistalker.it',
                      state: uuid(),
                      nonce: uuid(),
                    })
                      .then((res) => {
                        if (res.response && res.response.identityToken) {
                          const respData = res.response;
                          const authData = {
                            authorization: {
                              code: respData.authorizationCode,
                              id_token: respData.identityToken,
                              state: '',
                            },
                            user: {
                              email: respData.email,
                              name: { firstName: respData.givenName, lastName: respData.familyName, middleName: '' },
                            },
                          };
                          Analytics.sendEvent({ category: 'Login', action: 'Login with Apple', label: 'Login Form' });
                          dispatch(appleLogin(authData));
                        } else {
                          errorToastMessage(`Errore durante il login con Apple ${JSON.stringify(res)}`);
                          console.error('Apple login failed', res);
                        }
                      })
                      .catch((response) => {
                        errorToastMessage(`Errore durante il login con Apple: ${response}`);
                        console.error('Apple login failed', response);
                      });
                  }}
                >
                  <div className='text-white'>
                    <AppleIcon />
                  </div>
                  Entra con Apple
                </Button>
              )}
            </div>
          </div>

          <div>
            <Link className='cursor-pointer text-sm underline underline-offset-4' to='/password-dimenticata'>
              Hai dimenticato la password?
            </Link>
          </div>
        </div>

        <div className='border-accent-accent-4 bg-accent-accent-1 mt-4 flex flex-col items-start gap-4 self-stretch rounded-2xl border border-solid p-3'>
          <div className='flex w-full flex-col gap-2'>
            <div className='text-accent-accent-10 text-center text-sm'>Non hai un account?</div>
            <Link to='/registrati'>
              <Button className='border-accent-accent-10 bg-accent-accent-1 text-accent-accent-10 hover:bg-accent-accent-3 flex h-10 w-full cursor-pointer rounded-md border border-solid text-base'>
                Registrati
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='mt-10 flex w-full flex-col items-center justify-center'>
      {error && (
        <div className='border-red-9 bg-semantic-error-3 text-semantic-error-9 rounded-xl border border-solid p-3 text-center'>
          {error}
        </div>
      )}
      {content}
    </div>
  );
};

export default LoginForm;
